import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import './EventHero.css';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const TYPE_LABEL = { offline: 'In-person', online: 'Online', hybrid: 'Hybrid' };

function fmtRange(start, end) {
  if (!start) return '';
  const s = new Date(start);
  const e = end ? new Date(end) : s;
  if (s.toDateString() === e.toDateString()) {
    return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} – ${e.getDate()} ${s.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

const EventHero = () => {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    api.getEvents()
      .then((events) => {
        if (!Array.isArray(events) || events.length === 0) return;
        const now = Date.now();
        // Soonest event starting within 30 days.
        const upcoming = events
          .filter((e) => {
            const start = new Date(e.start_date).getTime();
            return start - now > 0 && start - now <= THIRTY_DAYS_MS;
          })
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
        if (upcoming.length > 0) setEvent(upcoming[0]);
      })
      .catch(() => { /* swallow */ });
  }, []);

  if (!event) return null;

  const isEnquire = event.pricing_mode === 'enquire';
  // An external registration link always wins, whatever the pricing mode.
  const ctaLabel = (event.registration_url || !isEnquire) ? 'Register now' : 'Enquire now';
  const facts = [
    { label: 'Dates', value: fmtRange(event.start_date, event.end_date) },
    ...(event.venue ? [{ label: 'Venue', value: event.venue }] : []),
    { label: 'Mode', value: TYPE_LABEL[event.event_type] || 'In-person' },
    ...(Array.isArray(event.highlights) ? event.highlights : []),
  ];

  return (
    <section className="event-billboard" aria-label="Upcoming event">
      <div className={`container event-billboard__inner${event.image ? '' : ' event-billboard__inner--no-media'}`}>
        {event.image && (
          <div className="event-billboard__media">
            <img src={event.image} alt={event.title} />
          </div>
        )}
        <div className="event-billboard__content">
          <span className="event-billboard__eyebrow">Upcoming event</span>
          <h2 className="event-billboard__title">{event.title}</h2>
          {event.subtitle && <p className="event-billboard__subtitle">{event.subtitle}</p>}

          <dl className="event-billboard__facts">
            {facts.map((f, i) => (
              <div className="event-billboard__fact" key={i}>
                {f.label && <dt>{f.label}</dt>}
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>

          <div className="event-billboard__actions">
            {event.registration_url ? (
              <a
                href={event.registration_url}
                target="_blank"
                rel="noopener noreferrer"
                className="event-billboard__cta"
              >
                {ctaLabel}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 17L17 7M9 7h8v8" />
                </svg>
              </a>
            ) : (
              <Link to={`/events/${event.slug}`} className="event-billboard__cta">
                {ctaLabel}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            )}
            <Link to={`/events/${event.slug}`} className="event-billboard__more">
              View details
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventHero;
